/**
 * @param {{ zone?: { warehouse?: string; location_id?: string | null } } | undefined} entry
 * @returns {boolean} false when a warehouse was resolved but Shopify location id is missing (misconfigured store)
 */
export function hasResolvableLocationForEntry(entry) {
  const z = entry?.zone;
  if (!z?.warehouse) return true;
  return Boolean(z.location_id);
}

export const STATES = {
  IDLE: "IDLE",
  FETCHING_SUGGESTIONS: "FETCHING_SUGGESTIONS",
  SUGGESTIONS_SHOWN: "SUGGESTIONS_SHOWN",
  RESOLVING_AVAILABILITY: "RESOLVING_AVAILABILITY",
  CHECKING_NSHIFT: "CHECKING_NSHIFT",
  CALENDAR_READY: "CALENDAR_READY",
  SESSION_COMPLETE: "SESSION_COMPLETE",
  SESSION_RESTORED: "SESSION_RESTORED",
  ERROR_OUTSIDE_ZONE: "ERROR_OUTSIDE_ZONE",
  ERROR_NO_AVAILABILITY: "ERROR_NO_AVAILABILITY",
};

const TRANSITIONS = {
  [STATES.IDLE]: {
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
    SESSION_LOADED: (ctx, { valid }) =>
      valid ? STATES.SESSION_RESTORED : STATES.IDLE,
    MANUAL_POSTCODE: () => STATES.RESOLVING_AVAILABILITY,
    DATE_SELECTED: () => STATES.SESSION_COMPLETE,
  },
  [STATES.FETCHING_SUGGESTIONS]: {
    SUGGESTIONS_RECEIVED: () => STATES.SUGGESTIONS_SHOWN,
    FETCH_ERROR: () => STATES.IDLE,
    RESET: () => STATES.IDLE,
  },
  [STATES.SUGGESTIONS_SHOWN]: {
    SUGGESTION_SELECTED: () => STATES.RESOLVING_AVAILABILITY,
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
    RESET: () => STATES.IDLE,
  },
  [STATES.RESOLVING_AVAILABILITY]: {
    ZONES_RESOLVED: (ctx, { zones, nshiftEnabled }) => {
      if (!zones || zones.length === 0) return STATES.ERROR_OUTSIDE_ZONE;
      if (nshiftEnabled) return STATES.CHECKING_NSHIFT;
      return STATES.CALENDAR_READY;
    },
    FETCH_ERROR: () => STATES.IDLE,
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
    RESET: () => STATES.IDLE,
  },
  [STATES.CHECKING_NSHIFT]: {
    NSHIFT_RESOLVED: (ctx, { dates }) =>
      dates.length === 0 ? STATES.ERROR_NO_AVAILABILITY : STATES.CALENDAR_READY,
    NSHIFT_ERROR: () => STATES.CALENDAR_READY,
    RECHECK: () => STATES.RESOLVING_AVAILABILITY,
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
    RESET: () => STATES.IDLE,
  },
  [STATES.CALENDAR_READY]: {
    DATE_SELECTED: () => STATES.SESSION_COMPLETE,
    NO_AVAILABILITY: () => STATES.ERROR_NO_AVAILABILITY,
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
    RECHECK: () => STATES.RESOLVING_AVAILABILITY,
    RESET: () => STATES.IDLE,
  },
  [STATES.SESSION_COMPLETE]: {
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
    RECHECK: () => STATES.RESOLVING_AVAILABILITY,
    RESET: () => STATES.IDLE,
  },
  [STATES.SESSION_RESTORED]: {
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
    RECHECK: () => STATES.RESOLVING_AVAILABILITY,
    RESET: () => STATES.IDLE,
  },
  [STATES.ERROR_OUTSIDE_ZONE]: {
    RESET: () => STATES.IDLE,
    RECHECK: () => STATES.RESOLVING_AVAILABILITY,
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
  },
  [STATES.ERROR_NO_AVAILABILITY]: {
    INPUT_CHANGE: (ctx, { query }) =>
      query.length >= 3 ? STATES.FETCHING_SUGGESTIONS : STATES.IDLE,
    RECHECK: () => STATES.RESOLVING_AVAILABILITY,
    RESET: () => STATES.IDLE,
  },
};

export function createStateMachine() {
  let current = STATES.IDLE;
  const context = {};

  return {
    get state() {
      return current;
    },
    get context() {
      return context;
    },
    transition(event, payload = {}) {
      const stateTransitions = TRANSITIONS[current];
      if (!stateTransitions) return;

      const handler = stateTransitions[event];
      if (!handler) return;

      current = handler(context, payload);
      Object.assign(context, payload);
    },
  };
}

const WH_WEEKLY_RE = /^([1-7]):(\d{2}:\d{2})-(\d{2}:\d{2})$/;
const WH_OPEN_DATE_RE = /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})-(\d{2}:\d{2})$/;
const WH_CLOSED_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isValidTime(time) {
  const [h, m] = time.split(":").map(Number);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

/**
 * Parse raw warehouse_schedule fields from the zones API response.
 * Same semantics as the server-side parseWarehouseSchedule.
 */
export function parseWarehouseSchedule(fields) {
  const weeklyHours = new Map();
  const closedDates = new Set();
  const openDates = new Map();

  const rawWeekly = (fields.weekly_hours ?? "").trim();
  const rawClosed = (fields.closed_dates ?? "").trim();
  const rawOpen = (fields.open_dates ?? "").trim();
  const rawBuffer = fields.production_buffer_hours ?? "0";

  let parsedBuffer = parseInt(rawBuffer, 10);
  if (!Number.isFinite(parsedBuffer) || parsedBuffer < 0) {
    if (rawBuffer !== "0" && rawBuffer !== "") {
      console.warn(`[warehouse-schedule] Invalid buffer "${rawBuffer}", clamping to 0`);
    }
    parsedBuffer = 0;
  }

  let hadNonEmptyField = false;
  let validLineCount = 0;

  if (rawWeekly) {
    hadNonEmptyField = true;
    for (const line of rawWeekly.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const m = trimmed.match(WH_WEEKLY_RE);
      if (!m) { console.warn(`[warehouse-schedule] weekly_hours: invalid "${trimmed}"`); continue; }
      const day = parseInt(m[1], 10);
      if (day < 1 || day > 7) { console.warn(`[warehouse-schedule] weekday ${day} out of range`); continue; }
      if (!isValidTime(m[2]) || !isValidTime(m[3])) { console.warn(`[warehouse-schedule] invalid time range in "${trimmed}"`); continue; }
      if (m[2] >= m[3]) { console.warn(`[warehouse-schedule] open >= close in "${trimmed}"`); continue; }
      if (weeklyHours.has(day)) { console.warn(`[warehouse-schedule] duplicate weekday ${day}`); continue; }
      weeklyHours.set(day, { open: m[2], close: m[3] });
      validLineCount++;
    }
  }

  if (rawClosed) {
    hadNonEmptyField = true;
    for (const line of rawClosed.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (!WH_CLOSED_DATE_RE.test(trimmed)) { console.warn(`[warehouse-schedule] closed_dates: invalid "${trimmed}"`); continue; }
      closedDates.add(trimmed);
      validLineCount++;
    }
  }

  if (rawOpen) {
    hadNonEmptyField = true;
    for (const line of rawOpen.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const m = trimmed.match(WH_OPEN_DATE_RE);
      if (!m) { console.warn(`[warehouse-schedule] open_dates: invalid "${trimmed}"`); continue; }
      if (!isValidTime(m[2]) || !isValidTime(m[3])) { console.warn(`[warehouse-schedule] open_dates: invalid time range "${trimmed}"`); continue; }
      if (m[2] >= m[3]) { console.warn(`[warehouse-schedule] open_dates: open >= close "${trimmed}"`); continue; }
      openDates.set(m[1], { open: m[2], close: m[3] });
      validLineCount++;
    }
  }

  let mode;
  if (!hadNonEmptyField) {
    mode = "blank_closed";
  } else if (validLineCount === 0) {
    console.error("[warehouse-schedule] All fields non-empty but no valid lines. Falling back to always-open.");
    mode = "invalid_always_open";
  } else {
    mode = "parsed";
  }

  return { mode, weeklyHours, closedDates, openDates, productionBufferHours: parsedBuffer };
}

/**
 * Is the warehouse open on this Stockholm date?
 * @param {object|undefined} schedule - parsed schedule or undefined
 * @param {string} dateKey - YYYY-MM-DD
 * @param {number} weekday - 1-7 ISO weekday
 */
export function isWarehouseOpen(schedule, dateKey, weekday) {
  if (!schedule) return true;
  if (schedule.mode === "invalid_always_open") return true;
  if (schedule.mode === "blank_closed") return schedule.openDates.has(dateKey);
  if (schedule.openDates.has(dateKey)) return true;
  if (schedule.closedDates.has(dateKey)) return false;
  return schedule.weeklyHours.has(weekday);
}

/**
 * Get effective same-day cutoff (close_time - buffer) as "HH:MM" or null.
 */
export function getEffectiveCutoff(schedule, dateKey, weekday) {
  if (!schedule) return null;
  if (schedule.mode === "invalid_always_open") return null;

  let hours;
  if (schedule.mode === "blank_closed") {
    hours = schedule.openDates.get(dateKey) ?? null;
  } else {
    const override = schedule.openDates.get(dateKey);
    if (override) { hours = override; }
    else if (schedule.closedDates.has(dateKey)) { return null; }
    else { hours = schedule.weeklyHours.get(weekday) ?? null; }
  }
  if (!hours) return null;
  const [closeH, closeM] = hours.close.split(":").map(Number);
  const totalMinutes = Math.max(0, closeH * 60 + closeM - schedule.productionBufferHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Derive ISO weekday (1=Mon, 7=Sun) from a YYYY-MM-DD dateKey using
 * a DST-safe method (noon avoids midnight edge cases).
 */
export function getStockholmWeekday(dateKey) {
  const d = new Date(dateKey + "T12:00:00");
  const day = d.getDay();
  return day === 0 ? 7 : day;
}

/**
 * Derive Stockholm HH:MM from an ISO server_now string.
 */
export function getStockholmTimeFromServerNow(serverNow) {
  const d = new Date(serverNow);
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const hr = parts.find((p) => p.type === "hour")?.value ?? "00";
  const min = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${hr}:${min}`;
}

/**
 * Pick the best warehouse with available dates, respecting zone priority.
 * When zones are provided, only warehouses with a matching zone entry are
 * eligible, and the one with the best (lowest) zone priority wins.
 * @param {Array} warehouses - nshift warehouse results
 * @param {Array} [zones] - zones from the zones endpoint (optional for legacy)
 */
export function pickWarehouse(warehouses, zones = []) {
  if (!warehouses || warehouses.length === 0) return null;

  const withDates = warehouses.filter((w) => w.available_dates?.length > 0);
  if (withDates.length === 0) return null;

  if (!zones.length) return withDates[0];

  const minPriority = new Map();
  for (const z of zones) {
    const p = z.priority ?? Infinity;
    const cur = minPriority.get(z.warehouse);
    if (cur === undefined || p < cur) minPriority.set(z.warehouse, p);
  }

  const eligible = withDates.filter((w) => minPriority.has(w.warehouse));
  if (eligible.length === 0) return null;

  eligible.sort((a, b) => minPriority.get(a.warehouse) - minPriority.get(b.warehouse));

  return eligible[0];
}

/**
 * Given a carrier list and a selected date, return the carrier_ids that can
 * deliver on that date.
 */
export function filterCarriersForDate(carriers, dateStr) {
  return carriers
    .filter((c) => c.available_dates?.some((d) => d.date === dateStr))
    .map((c) => c.carrier_id);
}

/**
 * Extracts the date portion of a value (YYYY-MM-DD or ISO datetime) and
 * returns the corresponding UTC midnight in ms. Returns NaN for invalid input.
 */
export function toUtcDayMs(value) {
  if (!value) return NaN;
  const isoDay = String(value).slice(0, 10);
  const [year, month, day] = isoDay.split("-").map(Number);
  if (!year || !month || !day) return NaN;
  return Date.UTC(year, month - 1, day);
}

const MAX_LOOKAHEAD_DAYS = 30;

/**
 * @param {object} schedule - parsed warehouse schedule
 * @param {string} todayDateStr - YYYY-MM-DD
 * @param {boolean} samedayViable - can the warehouse handle orders today?
 * @returns {string|null} YYYY-MM-DD handling date, or null if no open day within 30 days
 */
export function calculateHandlingDate(schedule, todayDateStr, samedayViable) {
  if (samedayViable) return todayDateStr;
  for (let offset = 1; offset <= MAX_LOOKAHEAD_DAYS; offset++) {
    const d = new Date(todayDateStr + "T12:00:00Z");
    d.setUTCDate(d.getUTCDate() + offset);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    const dateKey = `${y}-${m}-${day}`;
    const weekday = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    if (isWarehouseOpen(schedule, dateKey, weekday)) return dateKey;
  }
  return null;
}

/**
 * Derive location suffix ("0", "1", "n") from handling date offset.
 * @param {string} todayDate - YYYY-MM-DD
 * @param {string} handlingDate - YYYY-MM-DD
 * @returns {string}
 */
export function getLocationSuffix(todayDate, handlingDate) {
  const offset = Math.round((toUtcDayMs(handlingDate) - toUtcDayMs(todayDate)) / 86400000);
  if (offset <= 0) return "0";
  if (offset === 1) return "1";
  return "n";
}

/**
 * Strict D-1 handling date for a specific delivery date.
 * @param {ReturnType<typeof parseWarehouseSchedule>} schedule - parsed warehouse schedule
 * @param {string} todayDate - YYYY-MM-DD
 * @param {string} deliveryDate - YYYY-MM-DD
 * @param {boolean} canHandleToday - whether warehouse can still handle orders today
 * @returns {string|null} handling date or null when delivery cannot be fulfilled
 */
export function getHandlingDateForDelivery(schedule, todayDate, deliveryDate, canHandleToday) {
  if (deliveryDate === todayDate) return canHandleToday ? todayDate : null;
  const d = new Date(deliveryDate + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() - 1);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const dMinus1 = `${y}-${m}-${day}`;
  if (dMinus1 === todayDate) return canHandleToday ? todayDate : null;
  return isWarehouseOpen(schedule, dMinus1, getStockholmWeekday(dMinus1)) ? dMinus1 : null;
}

const SUFFIX_TO_KEY = { "0": "today", "1": "tomorrow", "n": "future" };

export function resolveAvailability(zones, schedule, now = new Date(), warehouseSchedules = {}) {
  const { min_days_ahead, max_days_ahead, blocked_dates } = schedule;
  const blockedSet = new Set(blocked_dates);
  const available = [];

  const nowHours = now.getHours();
  const nowMinutes = now.getMinutes();
  const nowTime = `${String(nowHours).padStart(2, "0")}:${String(nowMinutes).padStart(2, "0")}`;
  const todayStr = formatLocalDate(now);

  const parsedWS = new Map();
  for (const [wh, raw] of Object.entries(warehouseSchedules)) {
    parsedWS.set(wh, parseWarehouseSchedule(raw));
  }

  const canHandleTodayCache = new Map();
  for (const [wh, ws] of parsedWS.entries()) {
    const todayWeekday = getStockholmWeekday(todayStr);
    const canHandleToday = isWarehouseOpen(ws, todayStr, todayWeekday)
      && (() => {
        const cutoff = getEffectiveCutoff(ws, todayStr, todayWeekday);
        return !cutoff || nowTime < cutoff;
      })();
    canHandleTodayCache.set(wh, canHandleToday);
  }

  const sortedZones = [...zones].sort((a, b) => a.priority - b.priority);

  for (let offset = min_days_ahead; offset <= max_days_ahead; offset++) {
    const d = new Date(now);
    d.setDate(d.getDate() + offset);
    const dateStr = formatLocalDate(d);

    if (blockedSet.has(dateStr)) continue;

    const weekday = getStockholmWeekday(dateStr);
    let bestZone = null;

    for (const zone of sortedZones) {
      const ws = parsedWS.get(zone.warehouse);

      if (offset === 0 && ws && !canHandleTodayCache.get(zone.warehouse)) continue;

      let cutoff;

      if (offset === 0) {
        cutoff = zone.cutoff_time_0;
      } else if (offset === 1) {
        cutoff = zone.cutoff_time_1;
      } else {
        cutoff = zone.cutoff_time_n;
      }

      if (nowTime >= cutoff) continue;

      const handlingDate = ws
        ? getHandlingDateForDelivery(ws, todayStr, dateStr, canHandleTodayCache.get(zone.warehouse))
        : null;
      if (handlingDate === null && ws) continue;
      let locationSuffix, locationKey;
      if (handlingDate) {
        locationSuffix = getLocationSuffix(todayStr, handlingDate);
        locationKey = SUFFIX_TO_KEY[locationSuffix];
      } else {
        locationSuffix = offset === 0 ? "0" : offset === 1 ? "1" : "n";
        locationKey = offset === 0 ? "today" : offset === 1 ? "tomorrow" : "future";
      }

      bestZone = {
        warehouse: zone.warehouse,
        carrier_id: zone.carrier_id,
        carrier_name: zone.carrier_name,
        location: `${zone.warehouse}_${locationSuffix}`,
        location_id: zone.locations[locationKey],
      };
      break;
    }

    if (bestZone) {
      available.push({ date: dateStr, zone: bestZone });
    }
  }

  return available;
}

export function formatLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function buildCalendarGrid(
  year,
  month,
  {
    blockedDates = [],
    minDaysAhead = 0,
    maxDaysAhead = 30,
    today = new Date(),
    availableDates = null,
  } = {},
) {
  const blockedSet = new Set(blockedDates);
  const availableSet = Array.isArray(availableDates)
    ? new Set(availableDates.map((d) => (typeof d === "string" ? d : d.date)))
    : null;
  const firstDay = new Date(year, month, 1);
  const dayOfWeek = firstDay.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

  const startDate = new Date(year, month, 1 + mondayOffset);
  const grid = [];

  const todayStr = formatLocalDate(today);

  const earliest = new Date(today);
  earliest.setDate(earliest.getDate() + minDaysAhead);
  const earliestStr = formatLocalDate(earliest);

  const latest = new Date(today);
  latest.setDate(latest.getDate() + maxDaysAhead);
  const latestStr = formatLocalDate(latest);

  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let col = 0; col < 7; col++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + week * 7 + col);
      const dateStr = formatLocalDate(d);

      const isPast = dateStr < todayStr;
      const isBlocked = blockedSet.has(dateStr);
      const isOutOfRange = dateStr < earliestStr || dateStr > latestStr;
      const isUnavailable =
        availableSet !== null && !availableSet.has(dateStr);

      row.push({
        day: d.getDate(),
        month: d.getMonth(),
        year: d.getFullYear(),
        date: dateStr,
        disabled: isPast || isOutOfRange || isBlocked || isUnavailable,
        blocked: isBlocked && !isPast && !isOutOfRange,
        isCurrentMonth: d.getMonth() === month,
      });
    }
    grid.push(row);

    const lastInRow = row[row.length - 1];
    if (lastInRow.month > month || (lastInRow.month === 0 && month === 11)) {
      break;
    }
  }

  return grid;
}

const DEBOUNCE_MS = 150;

/** Human-readable duplicate of a raw `YYYY-MM-DD` date, for the visible line property. */
function formatVisibleDeliveryDate(dateStr) {
  try {
    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(`${dateStr}T12:00:00Z`));
  } catch {
    return dateStr;
  }
}

class DeliveryPicker extends HTMLElement {
  #sm = createStateMachine();
  #debounceTimer = null;
  #fetchController = null;
  #viewYear;
  #viewMonth;
  #selectedDate = null;
  #loadingText = "Loading…";
  #eventsBound = false;

  connectedCallback() {
    this.#loadingText = this.dataset.loadingText || this.#loadingText;
    const now = new Date();
    this.#viewYear = now.getFullYear();
    this.#viewMonth = now.getMonth();

    this.#bindEvents();
    this.#renderCalendar();
    this.#checkExistingSession();
    this.#syncQuickDateAvailability();

    this.querySelector("dialog-component")?.addEventListener(
      "dialog:open",
      this.#onDialogOpen,
    );

    this.addEventListener("dap:request-recheck", this.#onRequestRecheck);
    this.addEventListener("dap:request-reset", this.#onRequestReset);
  }

  disconnectedCallback() {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = null;
    this.#fetchController?.abort();
    this.#fetchController = null;

    this.removeEventListener("dap:request-recheck", this.#onRequestRecheck);
    this.removeEventListener("dap:request-reset", this.#onRequestReset);

    this.querySelector("dialog-component")?.removeEventListener(
      "dialog:open",
      this.#onDialogOpen,
    );
  }

  #bindEvents() {
    if (this.#eventsBound) return;
    this.#eventsBound = true;

    this.addEventListener("input", (e) => {
      if (e.target.closest("[data-dap-street]")) {
        this.#onStreetInput(e);
      }
    });

    this.addEventListener("change", (e) => {
      if (
        e.target.closest("[data-dap-address-type]") ||
        e.target.closest("[data-dap-address-type-company]")
      ) {
        this.#onAddressTypeChange();
      }
    });

    this.addEventListener(
      "blur",
      (e) => {
        if (e.target.closest("[data-dap-postcode]")) {
          this.#onManualPostcode(e);
        }
      },
      true,
    );

    this.addEventListener("click", (e) => {
      const prevBtn = e.target.closest("[data-dap-prev-month]");
      if (prevBtn) {
        this.#navigateMonth(-1);
        return;
      }

      const nextBtn = e.target.closest("[data-dap-next-month]");
      if (nextBtn) {
        this.#navigateMonth(1);
        return;
      }

      const dateBtn = e.target.closest("[data-dap-date]");
      if (dateBtn) {
        this.#onDateSelect(dateBtn.dataset.dapDate);
        return;
      }

      const quickDateBtn = e.target.closest("[data-dap-quick-date]");
      if (quickDateBtn) {
        if (
          quickDateBtn.dataset.dapQuickDate !== "other" &&
          !quickDateBtn.disabled
        ) {
          this.#onQuickDate(quickDateBtn.dataset.dapQuickDate);
        }
        return;
      }

      const suggestion = e.target.closest("[data-dap-suggestion]");
      if (suggestion) {
        this.#onSuggestionSelect(suggestion);
        return;
      }

      const addressDisplay = e.target.closest("[data-dap-address-display]");
      if (addressDisplay) {
        this.#hideAddressDisplay();
        return;
      }
    });

    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.#sm.transition("RESET");
        this.#render();
        return;
      }

      const listbox = this.querySelector("[role='listbox']");
      if (!listbox || listbox.hidden) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        this.#navigateSuggestions(e.key === "ArrowDown" ? 1 : -1);
      } else if (e.key === "Enter") {
        const active = listbox.querySelector("[aria-selected='true']");
        if (active) {
          e.preventDefault();
          this.#onSuggestionSelect(active);
        }
      }
    });
  }

  #checkExistingSession() {
    if (window.DAP?.isSessionValid()) {
      const session = window.DAP.getSession();
      Object.assign(this.#sm.context, {
        address: session?.address ?? this.#sm.context.address,
        postcode: session?.postcode ?? this.#sm.context.postcode,
        city: session?.city ?? this.#sm.context.city,
      });
      if (session?.date) {
        this.#selectedDate = session.date;
        const input = this.#dateInput;
        if (input) input.value = session.date;
        this.#syncViewToSelectedDate();
      }
      if (session?.address_type && ["personal", "company"].includes(session.address_type)) {
        const companyToggle = this.querySelector(
          "[data-dap-address-type-company]",
        );
        if (companyToggle) {
          companyToggle.checked = session.address_type === "company";
        } else {
          const radio = this.querySelector(
            `[data-dap-address-type][value="${session.address_type}"]`,
          );
          if (radio) radio.checked = true;
        }
      }
      if (session?.address) {
        const streetInput = this.querySelector("[data-dap-street]");
        if (streetInput) {
          const parts = [
            session.address,
            session.postcode,
            session.city,
          ].filter(Boolean);
          streetInput.value = parts.join(", ");
        }
        this.#showAddressDisplay(session);
      }
      this.#sm.transition("SESSION_LOADED", { valid: true });
      this.#syncQuickDateUI();
      this.#render();
      this.#onRequestRecheck();
      return;
    }
    this.#render();
  }

  async #onStreetInput(e) {
    const query = e.target.value.trim();
    clearTimeout(this.#debounceTimer);

    if (query.length < 3) {
      this.#fetchController?.abort();
      this.#sm.transition("INPUT_CHANGE", { query });
      this.#render();
      return;
    }

    this.#debounceTimer = setTimeout(async () => {
      this.#fetchController?.abort();
      this.#fetchController = new AbortController();
      const { signal } = this.#fetchController;

      this.#sm.transition("INPUT_CHANGE", { query });
      this.#render();

      try {
        const resp = await fetch(
          `/apps/delivery/geposit?q=${encodeURIComponent(query)}`,
          { signal },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (data.error) throw new Error(data.error);
        this.#sm.transition("SUGGESTIONS_RECEIVED", { suggestions: data });
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.warn("[DAP] Geposit fetch failed:", err?.message ?? err);
        this.#sm.transition("FETCH_ERROR");
      }
      this.#render();
    }, DEBOUNCE_MS);
  }

  #onAddressTypeChange() {
    const postcode = this.#sm.context.postcode;
    if (!postcode) return;
    this.#clearStaleSelection();
    this.#sm.transition("RECHECK");
    this.#resolveZones({
      address: this.#sm.context.address ?? "",
      postcode,
      city: this.#sm.context.city ?? "",
    });
  }

  #onManualPostcode(e) {
    const postcode = e.target.value.trim();
    if (!/^\d{5}$/.test(postcode)) return;

    const street = this.querySelector("[data-dap-street]")?.value || "";
    const city = this.querySelector("[data-dap-city]")?.value || "";

    this.#sm.transition("MANUAL_POSTCODE", { postcode });
    this.#resolveZones({ address: street, postcode, city });
  }

  #onSuggestionSelect(el) {
    let data;
    try {
      data = JSON.parse(el.dataset.dapSuggestion);
    } catch {
      return;
    }

    const streetInput = this.querySelector("[data-dap-street]");
    if (streetInput) {
      const parts = [data.address, data.postcode, data.city].filter(Boolean);
      streetInput.value = parts.join(", ");
    }

    this.#showAddressDisplay(data);
    this.#clearStaleSelection();

    this.#sm.transition("SUGGESTION_SELECTED", data);
    this.#render();
    this.#resolveZones(data);
  }

  #showAddressDisplay({ address, postcode, city }) {
    const display = this.querySelector("[data-dap-address-display]");
    const input = this.querySelector("[data-dap-street]");
    if (!display || !input) return;

    const meta = [postcode, city].filter(Boolean).join(" ");
    display.innerHTML =
      `<strong>${this.#esc(address)}</strong>` +
      (meta
        ? `<span class="dap-picker__suggestion-meta">${this.#esc(meta)}</span>`
        : "");
    display.hidden = false;
    input.style.visibility = "hidden";
  }

  #hideAddressDisplay() {
    const display = this.querySelector("[data-dap-address-display]");
    const input = this.querySelector("[data-dap-street]");
    if (!display || !input) return;

    display.hidden = true;
    input.style.visibility = "";
    input.value = "";

    this.#selectedDate = null;
    const dateInput = this.#dateInput;
    if (dateInput) dateInput.value = "";

    for (const btn of this.querySelectorAll("[data-dap-quick-date]")) {
      btn.setAttribute("aria-pressed", "false");
    }

    window.DAP?.clearSession();
    this.#sm.transition("RESET");
    this.#render();

    input.focus();
  }

  async #resolveZones({ address, postcode, city }, { preserveDate = false } = {}) {
    Object.assign(this.#sm.context, { address, postcode, city });
    this.#sm.context.availableDates = null;
    if (!preserveDate) this.#selectedDate = null;

    for (const btn of this.querySelectorAll("[data-dap-quick-date]")) {
      btn.disabled = true;
    }

    try {
      const params = new URLSearchParams({ postcode });
      const addrType = this.#selectedAddressType;
      if (addrType) params.set("address_type", addrType);
      const resp = await fetch(`/apps/delivery/zones?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const { zones, schedule, warehouse_schedules: wsRaw = {} } = data;

      const nshiftEnabled = this.dataset.nshiftEnabled === "true";

      this.#sm.transition("ZONES_RESOLVED", {
        zones,
        schedule,
        address,
        postcode,
        city,
        nshiftEnabled,
        warehouseSchedules: wsRaw,
      });

      if (this.#sm.state === STATES.CHECKING_NSHIFT && zones.length > 0) {
        await this.#checkNshift(zones, schedule, wsRaw);
      } else if (this.#sm.state === STATES.CALENDAR_READY) {
        const now = new Date();
        const dates = resolveAvailability(zones, schedule, now, wsRaw);
        if (dates.length === 0) {
          this.#sm.transition("NO_AVAILABILITY");
        } else {
          this.#sm.context.availableDates = dates;
        }
      }
    } catch {
      this.#sm.transition("FETCH_ERROR");
    }
    this.#syncQuickDateAvailability();
    this.#renderCalendar();
    this.#render();
  }

  async #checkNshift(zones, schedule, warehouseSchedules = {}) {
    this.#sm.context.nshiftWarehouse = null;
    this.#sm.context.nshiftWarehouses = null;
    this.#sm.context.nshiftServerNow = null;
    this.#sm.context.nshiftMode = null;

    const now = new Date();
    const candidates = resolveAvailability(zones, schedule, now, warehouseSchedules);
    const postcode = this.#sm.context.postcode;
    const addressType = this.#selectedAddressType;

    try {
      const resp = await fetch("/apps/delivery/nshift", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postcode, address_type: addressType }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // Pinned into the session below so validate/re-resolve honor
      // whichever mode actually resolved this selection, instead of a
      // live (and possibly since-flipped) shop setting.
      this.#sm.context.nshiftMode = data.mode ?? null;

      if (data.warehouses) {
        const selected = pickWarehouse(data.warehouses, zones);
        if (!selected || selected.available_dates.length === 0) {
          this.#sm.context.availableDates = [];
          this.#sm.transition("NSHIFT_RESOLVED", { dates: [] });
          return;
        }

        this.#sm.context.nshiftServerNow = data.server_now;

        const zonePriority = new Map();
        for (const z of zones) {
          const p = z.priority ?? Infinity;
          const cur = zonePriority.get(z.warehouse);
          if (cur === undefined || p < cur) zonePriority.set(z.warehouse, p);
        }

        const eligible = data.warehouses.filter(
          (w) => w.available_dates?.length > 0 && zonePriority.has(w.warehouse),
        );

        const whMap = new Map(eligible.map((w) => [w.warehouse, w]));
        this.#sm.context.nshiftWarehouses = whMap;
        this.#sm.context.nshiftWarehouse = selected;

        const candidateDateSet = new Set(candidates.map((c) => c.date));
        const nowTime = typeof data.server_now === "string" ? data.server_now.slice(11, 16) : "00:00";
        const todayStr = typeof data.server_now === "string" ? data.server_now.slice(0, 10) : formatLocalDate(new Date());

        for (const wh of eligible) {
          if (wh.same_day_available && !candidateDateSet.has(todayStr)) {
            const zone = zones.find((z) => z.warehouse === wh.warehouse);
            const cutoff = zone?.cutoff_time_0 ?? "23:59";
            if (nowTime < cutoff) {
              candidateDateSet.add(todayStr);
              break;
            }
          }
        }

        // candidateDateSet (from resolveAvailability) conflates several
        // independent reasons a date might be excluded: outside the shop's
        // min/max days-ahead window, explicitly blocked, or the generic D-1
        // handling-date rule (which has no concept of nShift Connections
        // and wrongly excludes a future same-day-Connection date once
        // today's cutoff has passed). Only the last of these should be
        // bypassed for an nShift-confirmed same-day date - the shop's
        // global range and blocklist still apply regardless of what nShift
        // offers.
        const inGlobalRangeDates = new Set();
        for (let offset = schedule.min_days_ahead; offset <= schedule.max_days_ahead; offset++) {
          const d = new Date(now);
          d.setDate(d.getDate() + offset);
          inGlobalRangeDates.add(formatLocalDate(d));
        }
        const blockedDateSet = new Set(schedule.blocked_dates);

        const dateToWarehouse = new Map();
        for (const wh of eligible) {
          const whPri = zonePriority.get(wh.warehouse) ?? Infinity;
          for (const dateStr of wh.available_dates) {
            const confirmedFutureSameDay =
              dateStr !== todayStr &&
              inGlobalRangeDates.has(dateStr) &&
              !blockedDateSet.has(dateStr) &&
              wh.carriers?.some((c) =>
                c.available_dates?.some(
                  (d) => d.date === dateStr && d.sameday === true,
                ),
              );
            if (!candidateDateSet.has(dateStr) && !confirmedFutureSameDay) continue;
            const existing = dateToWarehouse.get(dateStr);
            if (!existing || whPri < existing.priority) {
              dateToWarehouse.set(dateStr, { warehouse: wh, priority: whPri });
            }
          }
        }

        if (dateToWarehouse.size === 0) {
          this.#sm.context.availableDates = [];
          this.#sm.transition("NSHIFT_RESOLVED", { dates: [] });
          return;
        }

        const samedayWH = eligible.find((w) => w.same_day_available);
        this.#updateCutoffMessage(samedayWH ?? selected);

        const serverDayMs = toUtcDayMs(data.server_now);
        const serverTodayStr = todayStr;
        this.#sm.context.availableDates = [...dateToWarehouse.entries()].map(([dateStr, { warehouse: wh }]) => {
          const dateDayMs = toUtcDayMs(dateStr);
          const dayOffset = Number.isNaN(serverDayMs) || Number.isNaN(dateDayMs)
            ? 2
            : Math.round((dateDayMs - serverDayMs) / 86400000);

          const isDeliveryToday = dayOffset <= 0;
          const perDateEntry = wh.carriers
            ?.flatMap((c) => c.available_dates ?? [])
            ?.find((d) => d.date === dateStr);
          const handlingDate = perDateEntry?.handling_date !== undefined
            ? perDateEntry.handling_date
            : (isDeliveryToday ? wh.handling_date_if_today : wh.handling_date_if_later);

          let locationSuffix, locationKey;
          if (handlingDate === null) {
            return null;
          } else if (handlingDate === undefined) {
            const fallbackD = new Date(dateStr + "T12:00:00Z");
            fallbackD.setUTCDate(fallbackD.getUTCDate() - 1);
            const fbKey = `${fallbackD.getUTCFullYear()}-${String(fallbackD.getUTCMonth() + 1).padStart(2, "0")}-${String(fallbackD.getUTCDate()).padStart(2, "0")}`;
            const fallbackHD = dateStr === serverTodayStr ? serverTodayStr : fbKey;
            locationSuffix = getLocationSuffix(serverTodayStr, fallbackHD);
            locationKey = SUFFIX_TO_KEY[locationSuffix];
          } else {
            locationSuffix = getLocationSuffix(serverTodayStr, handlingDate);
            locationKey = SUFFIX_TO_KEY[locationSuffix];
          }

          const zoneForWH = zones.find((z) => z.warehouse === wh.warehouse);
          const dateCarrierIds = filterCarriersForDate(wh.carriers, dateStr);
          const dateCarrierNames = wh.carriers
            .filter((c) => dateCarrierIds.includes(c.carrier_id))
            .map((c) => c.carrier_name)
            .join(", ");

          return {
            date: dateStr,
            zone: {
              warehouse: wh.warehouse,
              carrier_id: dateCarrierIds.join(","),
              carrier_name: dateCarrierNames,
              location: `${wh.warehouse}_${locationSuffix}`,
              location_id: zoneForWH?.locations?.[locationKey] ?? null,
            },
          };
        }).filter(Boolean);

        this.#sm.transition("NSHIFT_RESOLVED", {
          dates: this.#sm.context.availableDates,
        });
      } else {
        const { available } = data;
        const availableSet = new Set(available);
        this.#sm.context.availableDates = candidates.filter((d) =>
          availableSet.has(d.date),
        );
        this.#sm.transition("NSHIFT_RESOLVED", {
          dates: this.#sm.context.availableDates,
        });
      }
    } catch {
      if (candidates.length === 0) {
        this.#sm.transition("NO_AVAILABILITY");
      } else {
        this.#sm.context.availableDates = candidates;
        this.#sm.transition("NSHIFT_ERROR");
      }
    }
  }

  #onDateSelect(dateStr) {
    this.#selectedDate = dateStr;
    this.#renderCalendar();
    const entry = this.#sm.context.availableDates?.find((d) => d.date === dateStr);
    const whForDate = this.#sm.context.nshiftWarehouses?.get(entry?.zone.warehouse) ?? this.#sm.context.nshiftWarehouse;
    this.#updateCutoffMessage(whForDate, dateStr);

    const input = this.#dateInput;
    if (input) input.value = dateStr;

    this.#commitSelection(dateStr);
  }

  #commitSelection(dateStr) {
    const entry = this.#sm.context.availableDates?.find(
      (d) => d.date === dateStr,
    );

    const missLoc = this.querySelector(".dap-picker__error-missing-location");
    if (missLoc) missLoc.hidden = true;

    if (!hasResolvableLocationForEntry(entry)) {
      if (missLoc) missLoc.hidden = false;
      this.#render();
      return;
    }

    this.#sm.transition("DATE_SELECTED", { date: dateStr });

    const whMap = this.#sm.context.nshiftWarehouses;
    const nshiftWH = whMap?.get(entry?.zone.warehouse) ?? this.#sm.context.nshiftWarehouse;
    let handlingDate = null;
    let samedayDelivery = false;
    let carrierId = entry?.zone.carrier_id ?? null;
    let nshiftSessionId = null;
    let nshiftOptionId = null;

    if (nshiftWH) {
      const carrierIds = filterCarriersForDate(nshiftWH.carriers, dateStr);
      carrierId = carrierIds.join(",") || carrierId;

      const dateEntry = nshiftWH.carriers
        .flatMap((c) => c.available_dates ?? [])
        .find((d) => d.date === dateStr);
      if (dateEntry?.handling_date !== undefined) {
        handlingDate = dateEntry.handling_date;
      } else {
        const isDeliveryToday = dateEntry?.sameday === true;
        handlingDate = isDeliveryToday
          ? nshiftWH.handling_date_if_today
          : nshiftWH.handling_date_if_later;
      }
      samedayDelivery = handlingDate === dateStr;
      // checkout mode only (see apps.delivery.nshift.tsx) - required to
      // register a partial shipment for nShift's conversion tracking.
      nshiftSessionId = dateEntry?.nshift_session_id ?? null;
      nshiftOptionId = dateEntry?.nshift_option_id ?? null;
    }

    const previous = window.DAP?.getSession?.() ?? {};
    const session = {
      address: this.#sm.context.address ?? previous.address ?? null,
      postcode: this.#sm.context.postcode ?? previous.postcode ?? null,
      city: this.#sm.context.city ?? previous.city ?? null,
      date: dateStr,
      warehouse: entry?.zone.warehouse ?? previous.warehouse ?? null,
      location: entry?.zone.location ?? previous.location ?? null,
      location_id: entry?.zone.location_id ?? previous.location_id ?? null,
      carrier_id: carrierId ?? previous.carrier_id ?? null,
      carrier_name: entry?.zone.carrier_name ?? previous.carrier_name ?? null,
      address_type: this.#selectedAddressType,
      handling_date: handlingDate ?? previous.handling_date ?? null,
      sameday_delivery: samedayDelivery,
      same_day_cutoff:
        nshiftWH?.same_day_cutoff ?? previous.same_day_cutoff ?? null,
      handling_cutoff:
        nshiftWH?.handling_cutoff ?? previous.handling_cutoff ?? null,
      nshift_session_id: nshiftSessionId ?? previous.nshift_session_id ?? null,
      nshift_option_id: nshiftOptionId ?? previous.nshift_option_id ?? null,
      // No fallback to previous.nshift_mode: this is reset to null on every
      // #checkNshift call and only set on a real, successful response for
      // *this* selection - carrying over a prior selection's mode here
      // would validate a never-nshift-confirmed (local-fallback) date
      // against a mode it was never actually resolved under.
      nshift_mode: this.#sm.context.nshiftMode,
      resolved_at: Date.now(),
    };

    window.DAP?.setSession(session);
    this.#populateHiddenInputs(session);
    this.#syncQuickDateUI();
    this.#updateCutoffMessage(nshiftWH, dateStr);

    this.dispatchEvent(
      new CustomEvent("dap:session-complete", {
        bubbles: true,
        composed: true,
        detail: session,
      }),
    );

    this.#render();
  }

  /** Human-readable duplicate of `_delivery_date` shown to the customer; label comes from `data-delivery-date-label`. */
  #visibleDeliveryDateProp(session) {
    const label = this.dataset.deliveryDateLabel;
    if (!label || !session.date) return {};
    return { [label]: formatVisibleDeliveryDate(session.date) };
  }

  #populateHiddenInputs(session) {
    const form = this.closest("form") || this;
    const map = {
      _delivery_date: session.date,
      _street_address: session.address,
      _postal_code: session.postcode,
      _city: session.city,
      _warehouse_location: session.location,
      _carrier: session.carrier_name,
      ...this.#visibleDeliveryDateProp(session),
    };

    for (const [key, value] of Object.entries(map)) {
      let input = form.querySelector(`input[name="properties[${key}]"]`);
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${key}]`;
        form.appendChild(input);
      }
      input.value = value ?? "";
    }
  }

  #navigateSuggestions(direction) {
    const listbox = this.querySelector("[role='listbox']");
    if (!listbox) return;
    const options = [...listbox.querySelectorAll("[role='option']")];
    if (options.length === 0) return;

    const current = options.findIndex(
      (o) => o.getAttribute("aria-selected") === "true",
    );
    const next = Math.max(0, Math.min(options.length - 1, current + direction));

    for (const o of options) o.setAttribute("aria-selected", "false");
    options[next].setAttribute("aria-selected", "true");

    const input = this.querySelector("[data-dap-street]");
    if (input) {
      const nextId = options[next].id;
      if (nextId) {
        input.setAttribute("aria-activedescendant", nextId);
      } else {
        input.removeAttribute("aria-activedescendant");
      }
    }
  }

  get #minDaysAhead() {
    return parseInt(this.dataset.minDaysAhead ?? "0", 10);
  }

  get #maxDaysAhead() {
    return parseInt(this.dataset.maxDaysAhead ?? "30", 10);
  }

  get #blockedDates() {
    try {
      return JSON.parse(this.dataset.blockedDates || "[]");
    } catch {
      return [];
    }
  }

  #renderCalendar() {
    const availableDates = this.#sm.context.availableDates ?? null;
    const availableDateStrs =
      availableDates?.map((a) => (typeof a === "string" ? a : a.date)) ?? null;

    const grid = buildCalendarGrid(this.#viewYear, this.#viewMonth, {
      blockedDates: this.#blockedDates,
      minDaysAhead: this.#minDaysAhead,
      maxDaysAhead: this.#maxDaysAhead,
      availableDates: availableDateStrs,
    });

    const calendarEl = this.querySelector("[role='grid']");
    if (!calendarEl) return;

    const locale = document.documentElement.lang || "sv-SE";
    const headerRow = `<div class="dap-picker__week-row" role="row">${Array.from(
      { length: 7 },
      (_, i) => {
        const d = new Date(2024, 0, i + 1); // 2024-01-01 is a Monday
        const name = d.toLocaleDateString(locale, { weekday: "short" });
        return `<span class="dap-picker__day-name" role="columnheader">${name}</span>`;
      },
    ).join("")}</div>`;

    const weekRows = grid
      .map((week) => {
        const cells = week
          .map((cell) => {
            const classes = ["dap-picker__day"];
            if (cell.disabled) classes.push("dap-picker__day--disabled");
            if (cell.blocked) classes.push("dap-picker__day--blocked");
            if (!cell.isCurrentMonth)
              classes.push("dap-picker__day--other-month");
            if (cell.date === this.#selectedDate)
              classes.push("dap-picker__day--selected");

            const attrs = !cell.isCurrentMonth
              ? `aria-hidden="true" tabindex="-1" disabled`
              : cell.disabled
                ? `aria-disabled="true" disabled`
                : `data-dap-date="${cell.date}" tabindex="0"`;

            return `<button type="button" class="${classes.join(" ")}" role="gridcell" ${attrs}>${cell.day}</button>`;
          })
          .join("");
        return `<div class="dap-picker__week-row" role="row">${cells}</div>`;
      })
      .join("");

    calendarEl.innerHTML = headerRow + weekRows;

    const monthLabel = this.querySelector("[data-dap-month-label]");
    if (monthLabel) {
      const d = new Date(this.#viewYear, this.#viewMonth, 1);
      monthLabel.textContent = d.toLocaleDateString(locale, {
        month: "long",
        year: "numeric",
      });
    }

    this.#updateMonthNavState();
  }

  #updateMonthNavState() {
    const now = new Date();
    const minYear = now.getFullYear();
    const minMonth = now.getMonth();

    const maxDate = new Date(now);
    maxDate.setDate(maxDate.getDate() + this.#maxDaysAhead);
    const maxYear = maxDate.getFullYear();
    const maxMonth = maxDate.getMonth();

    const prevBtn = this.querySelector("[data-dap-prev-month]");
    if (prevBtn) {
      prevBtn.disabled =
        this.#viewYear === minYear && this.#viewMonth === minMonth;
    }

    const nextBtn = this.querySelector("[data-dap-next-month]");
    if (nextBtn) {
      nextBtn.disabled =
        this.#viewYear === maxYear && this.#viewMonth === maxMonth;
    }
  }

  #navigateMonth(delta) {
    let targetMonth = this.#viewMonth + delta;
    let targetYear = this.#viewYear;
    if (targetMonth > 11) {
      targetMonth = 0;
      targetYear++;
    } else if (targetMonth < 0) {
      targetMonth = 11;
      targetYear--;
    }

    const now = new Date();
    const minYear = now.getFullYear();
    const minMonth = now.getMonth();
    if (
      targetYear < minYear ||
      (targetYear === minYear && targetMonth < minMonth)
    )
      return;

    const maxDate = new Date(now);
    maxDate.setDate(maxDate.getDate() + this.#maxDaysAhead);
    const maxYear = maxDate.getFullYear();
    const maxMonth = maxDate.getMonth();
    if (
      targetYear > maxYear ||
      (targetYear === maxYear && targetMonth > maxMonth)
    )
      return;

    this.#viewMonth = targetMonth;
    this.#viewYear = targetYear;
    this.#renderCalendar();
  }

  get #selectedAddressType() {
    const companyToggle = this.querySelector(
      "[data-dap-address-type-company]",
    );
    if (companyToggle) {
      return companyToggle.checked ? "company" : "personal";
    }
    const checked = this.querySelector("[data-dap-address-type]:checked");
    return checked?.value ?? "personal";
  }

  get #dateInput() {
    return this.querySelector("[data-dap-date-input]");
  }

  #render() {
    const state = this.#sm.state;
    this.dataset.state = state;

    this.#renderSuggestions();
    this.#renderStatus();
  }

  #renderSuggestions() {
    const listbox = this.querySelector("#dap-suggestions");
    if (!listbox) return;

    const state = this.#sm.state;
    const suggestions = this.#sm.context.suggestions ?? [];
    const streetInput = this.querySelector("[data-dap-street]");

    if (state === STATES.FETCHING_SUGGESTIONS) {
      listbox.innerHTML = `<li class="dap-picker__suggestion dap-picker__suggestion--loading" role="option" aria-disabled="true">
          <span class="dap-picker__loading-spinner" aria-hidden="true"></span>
          ${this.#esc(this.#loadingText)}
        </li>`;
      listbox.hidden = false;
      if (streetInput) streetInput.setAttribute("aria-expanded", "true");
      return;
    }

    if (state !== STATES.SUGGESTIONS_SHOWN || suggestions.length === 0) {
      listbox.innerHTML = "";
      listbox.hidden = true;
      if (streetInput) {
        streetInput.setAttribute("aria-expanded", "false");
        streetInput.removeAttribute("aria-activedescendant");
      }
      return;
    }

    listbox.innerHTML = suggestions
      .map(
        (s, i) =>
          `<li
            id="dap-suggestion-${i}"
            role="option"
            aria-selected="false"
            class="dap-picker__suggestion"
            data-dap-suggestion='${this.#esc(JSON.stringify(s)).replace(/'/g, "&#39;")}'
          >
            <strong>${this.#esc(s.address)}</strong>
            <span class="dap-picker__suggestion-meta">${this.#esc(s.postcode)} ${this.#esc(s.city)}</span>
          </li>`,
      )
      .join("");

    listbox.hidden = false;
    if (streetInput) streetInput.setAttribute("aria-expanded", "true");
  }

  #renderStatus() {
    const state = this.#sm.state;

    const outsideZone = this.querySelector(".dap-picker__error-outside-zone");
    if (outsideZone) outsideZone.hidden = state !== STATES.ERROR_OUTSIDE_ZONE;

    const noAvail = this.querySelector(".dap-picker__error-no-availability");
    if (noAvail) noAvail.hidden = state !== STATES.ERROR_NO_AVAILABILITY;
  }

  #onQuickDate(type) {
    const d = new Date();
    if (type === "tomorrow") d.setDate(d.getDate() + 1);
    const dateStr = formatLocalDate(d);
    this.#selectedDate = dateStr;
    const input = this.#dateInput;
    if (input) input.value = dateStr;
    this.#commitSelection(dateStr);
  }

  #syncQuickDateUI() {
    const todayStr = formatLocalDate(new Date());
    const tom = new Date();
    tom.setDate(tom.getDate() + 1);
    const tomorrowStr = formatLocalDate(tom);

    for (const btn of this.querySelectorAll("[data-dap-quick-date]")) {
      const type = btn.dataset.dapQuickDate;
      let pressed = false;
      if (this.#selectedDate) {
        if (type === "today") pressed = this.#selectedDate === todayStr;
        else if (type === "tomorrow")
          pressed = this.#selectedDate === tomorrowStr;
        else if (type === "other")
          pressed =
            this.#selectedDate !== todayStr &&
            this.#selectedDate !== tomorrowStr;
      }
      btn.setAttribute("aria-pressed", String(pressed));
    }

    const summary = this.querySelector("[data-dap-selected-summary]");
    if (summary) {
      if (this.#selectedDate) {
        const locale = document.documentElement.lang || "sv-SE";
        const [y, m, d] = this.#selectedDate.split("-").map(Number);
        const formattedDate = new Date(y, m - 1, d).toLocaleDateString(locale, {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        const template = summary.dataset.summaryTemplate || "__DATE__";
        summary.textContent = template.replace("__DATE__", formattedDate);
        summary.hidden = false;
      } else {
        summary.textContent = "";
        summary.hidden = true;
      }
    }
  }

  #syncQuickDateAvailability() {
    const todayStr = formatLocalDate(new Date());
    const tom = new Date();
    tom.setDate(tom.getDate() + 1);
    const tomorrowStr = formatLocalDate(tom);

    const todayBtn = this.querySelector("[data-dap-quick-date='today']");
    const tomorrowBtn = this.querySelector("[data-dap-quick-date='tomorrow']");
    const otherBtn = this.querySelector("[data-dap-quick-date='other']");
    const availableDates = this.#sm.context.availableDates;
    const disableAll =
      this.#sm.state === STATES.ERROR_OUTSIDE_ZONE ||
      this.#sm.state === STATES.ERROR_NO_AVAILABILITY ||
      this.#sm.state === STATES.RESOLVING_AVAILABILITY ||
      this.#sm.state === STATES.CHECKING_NSHIFT;

    if (disableAll) {
      if (todayBtn) todayBtn.disabled = true;
      if (tomorrowBtn) tomorrowBtn.disabled = true;
      if (otherBtn) otherBtn.disabled = true;
      return;
    }

    if (availableDates) {
      const availableSet = new Set(availableDates.map((d) => d.date));
      const noAvailability = availableSet.size === 0;
      if (todayBtn) todayBtn.disabled = noAvailability || !availableSet.has(todayStr);
      if (tomorrowBtn) tomorrowBtn.disabled = noAvailability || !availableSet.has(tomorrowStr);
      if (otherBtn) otherBtn.disabled = noAvailability;

      if (this.#selectedDate && !availableSet.has(this.#selectedDate)) {
        this.#clearStaleSelection();
      }
    } else {
      const minDays = this.#minDaysAhead;
      const maxDays = this.#maxDaysAhead;
      if (todayBtn) todayBtn.disabled = 0 < minDays || 0 > maxDays;
      if (tomorrowBtn) tomorrowBtn.disabled = 1 < minDays || 1 > maxDays;
      if (otherBtn) otherBtn.disabled = false;

      if (this.#selectedDate) {
        const selDate = new Date(this.#selectedDate + "T00:00:00");
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.round((selDate - today) / 86400000);
        if (diffDays < 0 || diffDays > maxDays) {
          this.#clearStaleSelection();
        }
      }
    }
  }

  #clearStaleSelection() {
    this.#selectedDate = null;
    const input = this.#dateInput;
    if (input) input.value = "";
    this.#syncQuickDateUI();
    window.DAP?.clearSession();
  }

  #syncViewToSelectedDate() {
    if (!this.#selectedDate) return;
    const [y, m] = this.#selectedDate.split("-").map(Number);
    this.#viewYear = y;
    this.#viewMonth = m - 1;
  }

  #onRequestRecheck = async () => {
    const { postcode, address, city } = this.#sm.context;
    if (!postcode) return;

    this.#sm.transition("RECHECK");
    await this.#resolveZones({ address, postcode, city }, { preserveDate: true });

    if (
      this.#selectedDate &&
      this.#sm.context.availableDates &&
      !this.#sm.context.availableDates.some((d) => d.date === this.#selectedDate)
    ) {
      this.#clearStaleSelection();
    }
  };

  #onRequestReset = () => {
    this.#selectedDate = null;
    const dateInput = this.#dateInput;
    if (dateInput) dateInput.value = "";

    const streetInput = this.querySelector("[data-dap-street]");
    if (streetInput) streetInput.value = "";

    const display = this.querySelector("[data-dap-address-display]");
    if (display) display.hidden = true;
    if (streetInput) streetInput.style.visibility = "";

    for (const btn of this.querySelectorAll("[data-dap-quick-date]")) {
      btn.setAttribute("aria-pressed", "false");
    }

    const postcodeInput = this.querySelector("[data-dap-postcode]");
    if (postcodeInput) postcodeInput.value = "";
    const cityInput = this.querySelector("[data-dap-city]");
    if (cityInput) cityInput.value = "";

    const missLoc = this.querySelector(".dap-picker__error-missing-location");
    if (missLoc) missLoc.hidden = true;

    const companyToggle = this.querySelector("[data-dap-address-type-company]");
    if (companyToggle) companyToggle.checked = false;
    const personalRadio = this.querySelector('[data-dap-address-type][value="personal"]');
    if (personalRadio) personalRadio.checked = true;

    const ctx = this.#sm.context;
    delete ctx.availableDates;
    delete ctx.postcode;
    delete ctx.address;
    delete ctx.city;
    delete ctx.nshiftWarehouse;
    delete ctx.nshiftWarehouses;
    delete ctx.nshiftServerNow;
    delete ctx.suggestions;

    window.DAP?.clearSession();
    this.#sm.transition("RESET");
    this.#syncQuickDateAvailability();
    this.#syncQuickDateUI();
    this.#updateCutoffMessage(null);
    this.#render();
  };

  #onDialogOpen = () => {
    this.#syncViewToSelectedDate();
    this.#renderCalendar();
    this.#onRequestRecheck();
  };

  #updateCutoffMessage(warehouse, selectedDate = null) {
    const el = this.querySelector("[data-dap-cutoff-message]");
    if (!el) return;

    const isSameDayDate = selectedDate
      ? this.#sm.context.availableDates?.find((d) => d.date === selectedDate)
          ?.zone?.location?.endsWith("_0") ?? false
      : true;

    if (warehouse?.same_day_available && warehouse?.same_day_cutoff && isSameDayDate) {
      const template = this.dataset.cutoffTemplate || "Beställ innan __TIME__ för leverans idag";
      el.textContent = template.replace("__TIME__", warehouse.same_day_cutoff);
      el.hidden = false;
    } else {
      el.textContent = "";
      el.hidden = true;
    }
  }

  #esc(str) {
    const d = document.createElement("div");
    d.textContent = str;
    return d.innerHTML;
  }
}

if (typeof customElements !== "undefined") {
  if (!customElements.get("delivery-picker")) {
    customElements.define("delivery-picker", DeliveryPicker);
  }
}